import React, { useState } from 'react';
import styled from '@emotion/styled';
import { collection, addDoc, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { threadsData } from '../utils/importThreadsData';
import { parseThreadInventory, isThreadInStock } from '../utils/parseThreadInventory';

const PageContainer = styled.div`
  padding: 40px;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  margin-top: 80px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Title = styled.h1`
  color: #62E07D;
  margin-bottom: 24px;
`;

const InfoBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  color: white;
  line-height: 1.8;
`;

const Button = styled.button`
  background-color: #62E07D;
  color: black;
  border: none;
  padding: 16px 32px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
  transition: background-color 0.3s ease;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;

  &:hover {
    background-color: #50B366;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 30px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
  margin: 24px 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #62E07D, #50B366);
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-weight: 600;
  font-size: 12px;
`;

const StatusMessage = styled.div`
  color: ${props => props.error ? '#FF6B6B' : props.success ? '#62E07D' : 'white'};
  padding: 16px;
  background: ${props => props.error ? 'rgba(255, 107, 107, 0.1)' : props.success ? 'rgba(98, 224, 125, 0.1)' : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 8px;
  margin-top: 16px;
  border: 1px solid ${props => props.error ? '#FF6B6B' : props.success ? '#62E07D' : 'rgba(255, 255, 255, 0.2)'};
`;

function ImportThreads() {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const checkExistingThreads = async () => {
    try {
      const threadsRef = collection(db, 'threads');
      const snapshot = await getDocs(threadsRef);
      return snapshot.size;
    } catch (error) {
      console.error('Error checking threads:', error);
      return 0;
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    setSuccess(false);
    setProgress(0);

    try {
      // Check if threads already exist
      const existingCount = await checkExistingThreads();
      if (existingCount > 0) {
        const confirm = window.confirm(
          `There are already ${existingCount} threads in the database. ` +
          `Importing will ADD ${threadsData.length} more threads (duplicates may occur). ` +
          `Do you want to continue?`
        );
        if (!confirm) {
          setImporting(false);
          return;
        }
      }

      // Get inventory data
      const inventory = parseThreadInventory();
      
      setStatus(`Starting import of ${threadsData.length} threads with inventory data...`);

      // Import threads in batches
      const batchSize = 50;
      let imported = 0;
      let inStockCount = 0;

      for (let i = 0; i < threadsData.length; i += batchSize) {
        const batch = threadsData.slice(i, i + batchSize);
        
        // Import each thread in the batch with inventory data
        await Promise.all(
          batch.map(thread => {
            const quantity = inventory[thread.threadNumber] || 0;
            const inStock = isThreadInStock(quantity);
            if (inStock) inStockCount++;
            
            return addDoc(collection(db, 'threads'), {
              ...thread,
              inStock: inStock,
              quantity: quantity > 0 ? quantity : null,
              location: null,
              name: null,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          })
        );

        imported += batch.length;
        const progressPercent = Math.round((imported / threadsData.length) * 100);
        setProgress(progressPercent);
        setStatus(`Imported ${imported} of ${threadsData.length} threads (${inStockCount} in stock)...`);
      }

      setSuccess(true);
      setStatus(`✅ Successfully imported ${imported} threads! (${inStockCount} marked as in stock)`);
      setProgress(100);
    } catch (err) {
      console.error('Import error:', err);
      setError(`Error during import: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <PageContainer>
      <Title>Import Madeira Polyneon Threads</Title>
      
      <InfoBox>
        <div style={{ marginBottom: '16px', fontSize: '14px' }}>
          <strong>Ready to import:</strong> {threadsData.length} Madeira Polyneon threads
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
          • All threads will be imported with their thread numbers and hex codes<br />
          • Your current inventory data will be automatically applied<br />
          • Threads with 2+ cones will be marked as "in stock"<br />
          • Quantities will be set from your inventory spreadsheet<br />
          • This is a one-time operation - you only need to run this once<br />
          • If you run it multiple times, it will create duplicates
        </div>
      </InfoBox>

      <Button onClick={handleImport} disabled={importing || success}>
        {importing ? 'Importing...' : success ? 'Import Complete!' : 'Start Import'}
      </Button>

      {(importing || progress > 0) && (
        <ProgressBar>
          <ProgressFill progress={progress}>
            {progress}%
          </ProgressFill>
        </ProgressBar>
      )}

      {status && (
        <StatusMessage success={success} error={!!error}>
          {status}
        </StatusMessage>
      )}

      {error && (
        <StatusMessage error>
          {error}
        </StatusMessage>
      )}

      {success && (
        <InfoBox style={{ marginTop: '24px', borderColor: '#62E07D' }}>
          <div style={{ fontSize: '14px' }}>
            <strong>Next Steps:</strong><br />
            1. Go to Thread Manager to view all imported threads<br />
            2. Update the threads you keep in stock by editing them<br />
            3. Add quantities and storage locations as needed<br />
            4. Use the Matcher to find closest thread colors!
          </div>
        </InfoBox>
      )}
    </PageContainer>
  );
}

export default ImportThreads;

